import { useState, useEffect } from "react";
import { integrations as api, type Integration } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { CheckCircle, XCircle, Linkedin, MessageSquare, Trash2, X, ExternalLink, Zap } from "lucide-react";
import { DOCS_URL } from "@/lib/siteConfig";

function StatusPill({ connected }: { connected: boolean }) {
  return connected
    ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-full"><CheckCircle size={12} /> Connected</span>
    : <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate bg-black/[0.04] px-2.5 py-1 rounded-full"><XCircle size={12} /> Not connected</span>;
}

function PlatformCard({ name, icon: Icon, description, accentColor, connected, children, onDisconnect }: {
  name: string; icon: React.ElementType; description: string; accentColor: string;
  connected: boolean; children?: React.ReactNode; onDisconnect?: () => void;
}) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accentColor}`}>
              <Icon size={22} />
            </div>
            <div>
              <p className="font-bold text-ink">{name}</p>
              <p className="text-xs text-slate mt-0.5">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusPill connected={connected} />
            {connected && onDisconnect && (
              <button onClick={onDisconnect}
                className="p-2 rounded-xl text-red-400 hover:bg-red-50 transition-colors" title="Disconnect">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function TwilioForm({ onSave }: { onSave: () => void }) {
  const [form, setForm] = useState({ accountSid: "", authToken: "", fromNumber: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inp = "w-full px-4 py-2.5 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30";

  const handleSave = async () => {
    if (!form.accountSid || !form.authToken || !form.fromNumber) { setError("All fields required"); return; }
    setSaving(true); setError(null);
    try { await api.connectTwilio(form); onSave(); }
    catch (e: any) { setError(e.message || "Failed to connect"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t border-black/[0.06] pt-5 mt-2 space-y-3">
      <p className="text-xs font-bold uppercase tracking-wider text-slate">Enter your Twilio credentials</p>
      <input placeholder="Account SID (ACxxxxx)" value={form.accountSid} onChange={(e) => setForm({ ...form, accountSid: e.target.value })} className={inp} />
      <input placeholder="Auth token" type="password" value={form.authToken} onChange={(e) => setForm({ ...form, authToken: e.target.value })} className={inp} />
      <input placeholder="From number (e.g. +15551234567)" value={form.fromNumber} onChange={(e) => setForm({ ...form, fromNumber: e.target.value })} className={inp} />
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button onClick={handleSave} disabled={saving}
        className="px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 transition-colors shadow-sm shadow-coral/20">
        {saving ? "Saving…" : "Save credentials"}
      </button>
    </div>
  );
}

export default function IntegrationsPage() {
  const [list, setList]           = useState<Integration[]>([]);
  const [loading, setLoading]     = useState(true);
  const [disconnecting, setDisc]  = useState<string | null>(null);
  const [linkedinLoading, setLI]  = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.list(); setList(r.integrations); }
    catch (e: any) { setError(e.message || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const isConnected = (p: string) => list.some((i) => i.platform === p && i.status === "active");

  const handleDisconnect = async (p: string) => {
    if (!confirm(`Disconnect ${p}?`)) return;
    setDisc(p);
    try {
      await api.disconnect(p);
      setList((prev) => prev.filter((i) => i.platform !== p));
    } catch (e: any) { setError(e.message || "Failed"); }
    finally { setDisc(null); }
  };

  const handleLinkedIn = async () => {
    setLI(true);
    try { const { url } = await api.getLinkedInOAuthUrl(); window.location.href = url; }
    catch (e: any) { setError(e.message || "LinkedIn not configured — check LINKEDIN_CLIENT_ID env var"); setLI(false); }
  };

  if (loading) return <Spinner className="h-screen" />;

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-lg sm:text-xl font-bold text-ink">Integrations</h1>
        <p className="text-xs sm:text-sm text-slate mt-0.5">Connect channels for cross-platform automation steps</p>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-4">
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)}><X size={14} /></button>
          </div>
        )}

        {/* LinkedIn */}
        <PlatformCard
          name="LinkedIn" icon={Linkedin}
          description="Send direct messages to 1st-degree connections in automation flows"
          accentColor="bg-[#0A66C2]/10 text-[#0A66C2]"
          connected={isConnected("linkedin")}
          onDisconnect={() => handleDisconnect("linkedin")}
        >
          {!isConnected("linkedin") && (
            <div className="border-t border-black/[0.06] pt-5 mt-2 space-y-3">
              <p className="text-xs text-slate leading-relaxed">
                Authorise MoreMail to send messages on your behalf. You'll be redirected to LinkedIn to approve access.
              </p>
              <div className="flex items-center gap-3">
                <button onClick={handleLinkedIn} disabled={linkedinLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A66C2] text-white text-sm font-semibold hover:bg-[#084d96] disabled:opacity-60 transition-colors shadow-sm">
                  <Linkedin size={15} />
                  {linkedinLoading ? "Redirecting…" : "Connect LinkedIn"}
                </button>
                <a href={`${DOCS_URL}/integrations/linkedin`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate hover:text-coral">
                  <ExternalLink size={12} /> Docs
                </a>
              </div>
            </div>
          )}
        </PlatformCard>

        {/* Twilio */}
        <PlatformCard
          name="Twilio SMS" icon={MessageSquare}
          description="Send SMS messages to subscribers with a phone number in their profile"
          accentColor="bg-red-50 text-red-600"
          connected={isConnected("twilio")}
          onDisconnect={() => handleDisconnect("twilio")}
        >
          {!isConnected("twilio") && <TwilioForm onSave={load} />}
        </PlatformCard>

        {/* How to use */}
        <div className="bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-amber-50"><Zap size={16} className="text-amber-600" /></div>
            <h2 className="font-bold text-ink">Using integrations in automations</h2>
          </div>
          <div className="space-y-3 text-sm text-slate">
            <p>Once connected, LinkedIn and SMS steps appear in the automation flow builder as additional step types.</p>
            <p>
              Recommended pattern: add a <strong className="text-ink">Condition</strong> step checking "Opened email" first.
              If the subscriber didn't open, route them to LinkedIn or SMS for follow-up.
            </p>
            <div className="bg-black/[0.02] rounded-xl p-3 text-xs space-y-1.5">
              <p className="font-semibold text-ink">Subscriber requirements</p>
              <p>LinkedIn steps need a <code className="bg-black/[0.04] px-1 rounded">linkedin_url</code> field.</p>
              <p>SMS steps need a <code className="bg-black/[0.04] px-1 rounded">phone</code> field (E.164 format).</p>
              <p>Both can be set via Subscribers page or the enrichment pipeline.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
